import { useMemo } from 'react';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { EVENT_CREW_ROLES, FLOW_CREW_ROLES } from '@/types/dtos/contributors/response/contributor.dto';
import {
  getAcceptedEventIdsForRoleMatch,
  getEventCrewRows,
  getFlowSettingsCrewRows,
  type CrewWidgetRow,
} from '@/utils/contributor-extraction.utils';
import { useGetFlowQuery } from '@/state/queries/flows/use-get-flow';
import { getObjectId } from '@/helpers/object.id.helper';

const normalizeId = (value?: unknown) => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ('id' in value && typeof (value as { id?: string }).id === 'string') {
      return (value as { id: string }).id;
    }
    if ('_id' in value && typeof (value as { _id?: string })._id === 'string') {
      return (value as { _id: string })._id;
    }
    if ('$oid' in value && typeof (value as { $oid?: string }).$oid === 'string') {
      return (value as { $oid: string }).$oid;
    }
  }
  return undefined;
};

/** Event dashboard: event crew roles only. */
export function useEventCrewWidgetSynced(): { crewRows: CrewWidgetRow[]; eventId: string | undefined } {
  const { contributors, event } = useEventStore();
  const acceptedEventIds = useMemo(() => getAcceptedEventIdsForRoleMatch(event), [event]);
  const eventId = useMemo(
    () => event?.id ?? (event?.origin != null ? getObjectId(event.origin as string | { id: string }) : undefined),
    [event],
  );

  const crewRows = useMemo(() => {
    if (!contributors?.length || acceptedEventIds.size === 0) return [];
    return getEventCrewRows(contributors, [...EVENT_CREW_ROLES], acceptedEventIds);
  }, [contributors, acceptedEventIds]);

  return { crewRows, eventId };
}

/** Flow settings: flow crew roles; for single-flow events, also event crew. */
export function useFlowCrewWidgetSynced(
  flow: FlowDto,
  eventSlotId?: string | null,
): { crewRows: CrewWidgetRow[]; flowForRoles: FlowDto; flowEntityId: string; eventId: string | undefined } {
  const { contributors, event } = useEventStore();
  const acceptedEventIds = useMemo(() => getAcceptedEventIdsForRoleMatch(event), [event]);
  const eventId = useMemo(
    () => event?.id ?? (event?.origin != null ? getObjectId(event.origin as string | { id: string }) : undefined),
    [event],
  );
  const flowOriginId = normalizeId(flow.origin) || flow.id;

  const { data: freshFlow } = useGetFlowQuery(flowOriginId, eventSlotId ?? undefined, {
    staleTime: 0,
    refetchOnMount: true,
  });
  const displayFlow = freshFlow ?? flow;
  const flowEntityId = normalizeId(displayFlow.origin) || displayFlow.id;
  const flowForRoles = useMemo(
    () => ({
      ...displayFlow,
      id: flowEntityId,
      origin: flowEntityId,
    }),
    [displayFlow, flowEntityId],
  );

  const flowCrewRoleIds = useMemo(() => [...FLOW_CREW_ROLES] as readonly string[], []);

  const crewRows = useMemo(() => {
    if (!contributors?.length) return [];
    return getFlowSettingsCrewRows(flowForRoles, acceptedEventIds, contributors, event?.multiFlow, flowCrewRoleIds);
  }, [flowForRoles, acceptedEventIds, contributors, event?.multiFlow, flowCrewRoleIds]);

  return { crewRows, flowForRoles, flowEntityId, eventId };
}
